import React, { useRef, useEffect, PropsWithChildren } from 'react';

/**
 * Hook that alerts clicks outside of the passed ref
 */
const useOutsideAlerter = (ref: any, onClick: (a?: any) => void) => {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClick();
      }
    };
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onClick]);
};

interface OutsideAlerterProps {
  onBlur: (a?: any) => void;
}

/**
 * OutsideAlerter is an empty div that will trigger a function when you click outside of it. Mainly use to close dropdowns.
 * @param {(a?: any) => void} onBlur the callback definition that triggers once you click outside
 * @returns {React.FC<OutsideAlerterProps>} OutsideAlerter
 */
export const OutsideAlerter: React.FC<
  OutsideAlerterProps & PropsWithChildren
> = ({ children, onBlur }) => {
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, onBlur);

  return <div ref={wrapperRef}>{children}</div>;
};
